/**
 * The Edit form's rules — customer-portal review #75 (fix round P), as pure functions.
 *
 * The form REUSES the new-agent wizard's rules (`../new/wizard-rules.ts`) rather than restating
 * them: the same `WizardContext`, the same `resolve`, the same content and language problems, the
 * same field formats and week editor. What is different about an edit is said here:
 *
 *   · THE ANSWERS START FROM A BUILT VERSION (`GET /v1/agents/{id}/answers`), not from nothing.
 *   · THE DOCUMENT IS KEPT unless a new file is picked. A kept document counts as attached, so the
 *     wizard's "attach a file" problem does not fire; picking a file replaces it.
 *   · A TEXT FIELD THE LIVE CONFIGURATION DOES NOT CARRY starts empty and is asked again.
 *   · WHAT IS SENT: an edit (`POST …/edit`) when anything that needs a build changed; the
 *     "Record calls" switch alone (`PATCH /v1/agents/{id}`, published at once, no build) when that is
 *     the only change on the live version; nothing when nothing changed.
 *   · A VOICE PER LANGUAGE (plan-015 G1U, gap G1): the form starts from the version's `voice_by_language`, each language
 *     keeps or changes its own voice, and the edit sends the whole map. Changing any language's voice needs a build.
 *   · THE INFORMATION INPUTS (live bug 2026-10-14, plan-018): a "Customer support" agent built from its website alone
 *     could not be saved — the form drew the address but never sent it, so the api found no member of the required
 *     group "information" given. Now the website address is sent as typed (its stored form; empty when cleared, which
 *     removes it), and each kept document is counted for the upload it was attached to ({@link keptDocumentOf}): the
 *     pasted text by its `<code>--` name, the rest for the upload that takes documents — every one of them for an upload
 *     that takes several. A kept upload is never a change; picking a file replaces every kept document.
 */
import type {
  AgentTemplateCardView,
  LanguagePackView,
  VoiceView,
} from "../../../api/gateway.js";
import type { VoiceAgentAnswerView, VoiceAgentEditRequestView } from "../../../api/slice/voice-agent-edit.js";
import { fieldFormatOf } from "../new/field-format.js";
import { closedWeek, openingHoursText, openingWeekOf } from "../new/opening-hours.js";
import type { OpeningWeek } from "../new/opening-hours.js";
import type { WizardAnswer } from "../new/wizard-draft.js";
import { websiteAddressSentOf } from "../new/information-input.js";
import {
  CONTENT_STEP,
  LANGUAGE_STEP,
  attachedNameOf,
  contentFieldOf,
  fileSlotOf,
  uploadFileOf,
  orderBookFieldOf,
  problemOf,
  takesSeveralFiles,
  type Resolved,
  type WizardContext,
} from "../new/wizard-rules.js";

/** The first upload field: the document the agent is built from. */
export function primaryUploadCodeOf(card: AgentTemplateCardView): string | null {
  return card.fieldSet.find((one) => one.kind === "upload")?.code ?? null;
}

type UploadField = Extract<AgentTemplateCardView["fieldSet"][number], { kind: "upload" }>;

function uploadFieldOf(card: AgentTemplateCardView): readonly UploadField[] {
  return card.fieldSet.filter((one): one is UploadField => one.kind === "upload");
}

/**
 * The stored file names of the version's documents, by the upload field each was attached to — read from the names the
 * wizard gave them (`uploadFileOf`):
 *
 *   · an upload that may be typed (`allowText`) holds the documents named `<code>--…`;
 *   · every other document belongs to the first upload that may not be typed — all of them when it takes several
 *     files (the support template's "Documents"), the first otherwise (a delivery menu);
 *   · with no such upload, to the first upload when nothing else holds it (a questionnaire stored before the prefix).
 */
export function keptDocumentOf(card: AgentTemplateCardView, view: VoiceAgentAnswerView): Readonly<Record<string, readonly string[]>> {
  const upload = uploadFieldOf(card);
  const kept: Record<string, readonly string[]> = {};
  const claimed = new Set<string>();
  for (const field of upload) {
    if (field.allowText !== true) continue;
    const named = view.document.map((one) => one.filename).filter((name) => name.startsWith(`${field.code}--`));
    if (named.length === 0) continue;
    kept[field.code] = named.slice(0, 1);
    for (const name of named) claimed.add(name);
  }
  const rest = view.document.map((one) => one.filename).filter((name) => !claimed.has(name));
  if (rest.length === 0) return kept;
  const documentField = upload.find((one) => one.allowText !== true) ?? upload.find((one) => kept[one.code] === undefined);
  if (documentField === undefined) return kept;
  kept[documentField.code] = takesSeveralFiles(documentField) ? rest.slice(0, documentField.maxFileCount ?? 1) : rest.slice(0, 1);
  return kept;
}

/** The kept documents as the form's upload values: a typed upload's name without its prefix, several names one per line. */
function keptUploadValueOf(card: AgentTemplateCardView, view: VoiceAgentAnswerView): Readonly<Record<string, string>> {
  const value: Record<string, string> = {};
  for (const [code, name] of Object.entries(keptDocumentOf(card, view))) {
    const field = uploadFieldOf(card).find((one) => one.code === code);
    if (field === undefined) continue;
    const prefix = `${code}--`;
    value[code] = field.allowText === true
      ? name.map((one) => (one.startsWith(prefix) ? one.slice(prefix.length) : one)).join("\n")
      : name.join("\n");
  }
  return value;
}

/**
 * The upload values the form starts from, for every upload field — what "Keep … instead" puts back after a file was
 * picked (an upload with no kept document starts empty).
 */
export function keptUploadAnswerOf(card: AgentTemplateCardView, view: VoiceAgentAnswerView): Readonly<Record<string, string>> {
  const kept = keptUploadValueOf(card, view);
  return Object.fromEntries(uploadFieldOf(card).map((field) => [field.code, kept[field.code] ?? ""]));
}

/**
 * The form's upload values once the first file is picked for `pickedCode`: picking replaces every kept document, so an
 * upload that is one of a required group's alternatives lets its kept names go (the group answers for it); any other
 * upload keeps its name and the wizard's rule asks for its file again.
 */
export function replacedUploadAnswerOf(
  card: AgentTemplateCardView,
  view: VoiceAgentAnswerView,
  fieldValue: Readonly<Record<string, string>>,
  pickedCode: string,
): Readonly<Record<string, string>> {
  const kept = keptUploadValueOf(card, view);
  const next: Record<string, string> = { ...fieldValue };
  for (const field of uploadFieldOf(card)) {
    if (kept[field.code] === undefined || fieldValue[field.code] !== kept[field.code]) continue;
    if (field.code === pickedCode ? takesSeveralFiles(field) : field.requiredGroup !== undefined) next[field.code] = "";
  }
  return next;
}

/** The form's first answers: the built version's own. */
export function editAnswerOf(view: VoiceAgentAnswerView, card: AgentTemplateCardView): WizardAnswer {
  const openingWeek: Record<string, OpeningWeek> = {};
  for (const field of card.fieldSet) {
    if (field.kind !== "text" || fieldFormatOf(field) !== "opening_hours") continue;
    // A day with more periods than the editor draws reads back as `null`: the week starts closed
    // and the form asks for it again, rather than silently dropping a period.
    openingWeek[field.code] = openingWeekOf(view.openingWeek[field.code]) ?? closedWeek();
  }
  const fieldValue: Record<string, string> = { ...view.fieldValue, ...keptUploadValueOf(card, view) };
  return {
    displayName: null,
    fieldValue,
    openingWeek,
    languageCode: view.languageCode,
    voiceId: view.voiceId,
    voiceByLanguage: view.voiceByLanguage,
    greeting: view.greeting,
    numberChoice: "bring_my_own",
    medium: ["voice"],
    channelUnitCount: null,
    recordingEnabled: view.recordingEnabled,
    paymentMethod: view.paymentMethod,
  };
}

export interface EditContextInput {
  readonly card: AgentTemplateCardView;
  readonly view: VoiceAgentAnswerView;
  readonly answer: WizardAnswer;
  /** A new file picked for an upload field: the document is replaced. */
  readonly file: Readonly<Record<string, File>>;
  readonly languagePack: readonly LanguagePackView[];
  readonly voice: readonly VoiceView[];
}

/** Whether the version's documents are kept: no new file was picked. */
export function documentsChoiceOf(file: Readonly<Record<string, File>>): "keep" | "replace" {
  return Object.keys(file).length === 0 ? "keep" : "replace";
}

/** The file slots the kept documents fill: the field code, or `<code>/<name>` for each file of an upload that takes several. */
function keepingSlotOf(card: AgentTemplateCardView, view: VoiceAgentAnswerView): readonly string[] {
  const value = keptUploadValueOf(card, view);
  return uploadFieldOf(card).flatMap((field) => {
    const kept = value[field.code];
    if (kept === undefined) return [];
    return takesSeveralFiles(field) ? kept.split("\n").map((name) => fileSlotOf(field, name)) : [field.code];
  });
}

/**
 * The wizard's context, for an edit. A kept document is "already confirmed" — exactly the wizard's
 * resumed-build case — so the upload is attached without a file; the medium and number steps are
 * not part of an edit, so their inputs are fixed.
 */
export function editContextOf(input: EditContextInput): WizardContext {
  const keeping = documentsChoiceOf(input.file) === "keep" && input.view.document.length > 0;
  const kept = keepingSlotOf(input.card, input.view);
  return {
    card: input.card,
    answer: input.answer,
    file: input.file,
    created: keeping ? { voiceAgentId: input.view.voiceAgentId, buildJobId: "", confirmedField: kept } : null,
    languagePack: input.languagePack,
    voice: input.voice,
    suggestedLanguage: null,
    ceiling: 1,
    newNumberAvailable: false,
    accountName: "",
    otherName: [],
  };
}

/** Every problem the edit still has, keyed by control: the content step's and the language step's. */
export function editProblemOf(context: WizardContext, resolved: Resolved): Readonly<Record<string, string>> {
  return { ...problemOf(CONTENT_STEP, context, resolved), ...problemOf(LANGUAGE_STEP, context, resolved) };
}

/** What Save sends. Call it only when {@link editProblemOf} is empty. */
export function editRequestOf(
  context: WizardContext,
  resolved: Resolved,
  view: VoiceAgentAnswerView,
): VoiceAgentEditRequestView {
  const { card, answer } = context;
  const fieldValue: Record<string, string> = {};
  const replacing = documentsChoiceOf(context.file) === "replace";
  const kept = keptDocumentOf(card, view);
  for (const field of contentFieldOf(card, answer.fieldValue)) {
    if (field.kind === "choice") {
      fieldValue[field.code] = answer.fieldValue[field.code] ?? field.defaultValue;
      continue;
    }
    if (field.kind === "website") {
      // Always sent: the address as stored, or empty when the box is empty — which removes the base version's.
      fieldValue[field.code] = websiteAddressSentOf(answer.fieldValue[field.code] ?? "") ?? "";
      continue;
    }
    if (field.kind === "upload" && replacing) {
      // Replacing: the files picked now are all the new version has, named as the wizard names them.
      if (takesSeveralFiles(field)) {
        const attached = attachedNameOf(context, field);
        if (attached.length > 0) fieldValue[field.code] = attached.join("\n");
        continue;
      }
      const picked = context.file[field.code];
      if (picked !== undefined && picked.size > 0) fieldValue[field.code] = uploadFileOf(field, picked).name;
      continue;
    }
    if (field.kind === "upload" && field.allowText === true) {
      const keptName = kept[field.code]?.[0];
      if (keptName !== undefined) fieldValue[field.code] = keptName;
      continue;
    }
    if (field.kind !== "text") continue;
    fieldValue[field.code] =
      fieldFormatOf(field) === "opening_hours"
        ? openingHoursText(answer.openingWeek[field.code] ?? closedWeek())
        : (answer.fieldValue[field.code] ?? "").trim();
  }
  const orderBook = orderBookFieldOf(card);
  if (orderBook !== undefined) fieldValue[orderBook.code] = "book";
  return {
    agentVersionId: view.liveVersionId === view.agentVersionId ? null : view.agentVersionId,
    expectedLiveVersionSeq: view.liveVersionSeq,
    fieldValue,
    languageCode: resolved.languageCode,
    voiceByLanguage: resolved.voiceByLanguage,
    greeting: resolved.greeting,
    recordingEnabled: answer.recordingEnabled,
    paymentMethod: orderBook === undefined ? null : (answer.paymentMethod ?? []),
    documents: documentsChoiceOf(context.file),
  };
}

function sameSet(one: readonly string[], other: readonly string[]): boolean {
  return one.length === other.length && one.every((each) => other.includes(each));
}

function sameVoiceMap(one: Readonly<Record<string, string>>, other: Readonly<Record<string, string>>): boolean {
  const key = Object.keys(one);
  return key.length === Object.keys(other).length && key.every((code) => other[code] === one[code]);
}

/**
 * What the edit changes, in the form's terms: build-worthy answers, and the recording switch. An upload's value is
 * never compared — the answers read back carry none; a picked file is the `replace` choice.
 */
export function pendingChangeOf(
  request: VoiceAgentEditRequestView,
  view: VoiceAgentAnswerView,
  card: AgentTemplateCardView,
): { readonly needsBuild: boolean; readonly recordingChanged: boolean } {
  const upload = new Set(uploadFieldOf(card).map((one) => one.code));
  const textChanged = Object.entries(request.fieldValue).some(([code, value]) => !upload.has(code) && (view.fieldValue[code] ?? "") !== value);
  const paymentChanged =
    request.paymentMethod !== null && (view.paymentMethod === null || !sameSet(request.paymentMethod, view.paymentMethod));
  const needsBuild =
    request.documents === "replace" ||
    view.unrecoveredField.length > 0 ||
    textChanged ||
    paymentChanged ||
    request.greeting !== view.greeting ||
    !sameVoiceMap(request.voiceByLanguage, view.voiceByLanguage) ||
    request.languageCode.length !== view.languageCode.length ||
    request.languageCode.some((code, at) => view.languageCode[at] !== code);
  return { needsBuild, recordingChanged: request.recordingEnabled !== view.recordingEnabled };
}

/**
 * The act Save performs:
 *
 *   `build`      something the prompt, the catalogue or the voice depends on changed → a new version;
 *   `recording`  only "Record calls" changed on the LIVE version → the switch, published at once;
 *   `none`       nothing changed.
 *
 * The recording switch forks the live version, so from any other version a recording-only change is
 * sent as an edit, and the server says what to do instead.
 */
export function saveActOf(
  request: VoiceAgentEditRequestView,
  view: VoiceAgentAnswerView,
  card: AgentTemplateCardView,
): "build" | "recording" | "none" {
  const pending = pendingChangeOf(request, view, card);
  if (pending.needsBuild) return "build";
  if (!pending.recordingChanged) return "none";
  return view.liveVersionId === view.agentVersionId ? "recording" : "build";
}
